import Shepherd from 'shepherd.js';
type Tour = InstanceType<typeof Shepherd.Tour>;

/** Shared button classes for consistent styling */
const BTN_PRIMARY = 'shepherd-button-primary';
const BTN_SECONDARY = 'shepherd-button-secondary';

/**
 * Home page tour steps (segment 1 of 2).
 * The final step's "Open Strategist" button is wired up by TourController
 * to navigate to /strategist and start the strategist segment.
 */
export const getHomeSteps = (onNavigateToStrategist: () => void) => [
  {
    id: 'welcome',
    title: 'Welcome to SignalDeck! 🚀',
    text: "Let's take a quick tour of your dashboard. You can skip at any time.",
    buttons: [
      {
        text: 'Skip Tour',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).cancel();
        },
      },
      {
        text: "Let's Go",
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
  },
  {
    id: 'hero-grid',
    attachTo: { element: '[data-tour="hero-grid"]', on: 'bottom' as const },
    title: 'Your Command Center',
    text: 'These six cards are your main tools — pre-market analysis, trading, positions, P&L tracking, bot management, and the AI Strategist.',
    buttons: [
      {
        text: 'Skip',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).cancel();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'card-predictions',
    attachTo: { element: '[data-tour="card-predictions"]', on: 'bottom' as const },
    title: 'Pre-Market Brief 🔮',
    text: 'Start your day here. AI-powered sentiment analysis and market news before the bell rings.',
    buttons: [
      {
        text: 'Back',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).back();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'card-quick-trade',
    attachTo: { element: '[data-tour="card-quick-trade"]', on: 'bottom' as const },
    title: 'Quick Trade ⚡',
    text: 'Buy or sell any stock or ETF with a single tap. Claude and GPT both weigh in before you confirm.',
    buttons: [
      {
        text: 'Back',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).back();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'card-strategist',
    attachTo: { element: '[data-tour="card-strategist"]', on: 'bottom' as const },
    title: 'AI Strategist 🧠',
    text: "Claude autonomously analyzes 100+ symbols and recommends trades based on technicals, sentiment, and macro conditions. Let's go check it out!",
    buttons: [
      {
        text: 'Skip',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).cancel();
        },
      },
      {
        text: 'Open Strategist →',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).hide();
          onNavigateToStrategist();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
];

/**
 * Strategist page tour steps (segment 2 of 2).
 * Called after cross-page navigation from Home.
 */
export const getStrategistSteps = (onFinish: () => void) => [
  {
    id: 'strategist-header',
    attachTo: { element: '[data-tour="strategist-header"]', on: 'bottom' as const },
    title: 'The AI Strategist',
    text: 'Claude scans the market every 5 minutes using 9 specialized AI agents — analyzing technicals, sentiment, macro conditions, and more.',
    buttons: [
      {
        text: 'Skip',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).cancel();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'mode-toggle',
    attachTo: { element: '[data-tour="mode-toggle"]', on: 'bottom' as const },
    title: 'Paper vs Live Mode',
    text: "Start with Paper mode to see Claude's recommendations without risking real money. Switch to Live when you're confident.",
    buttons: [
      {
        text: 'Back',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).back();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'config-panel',
    attachTo: { element: '[data-tour="config-panel"]', on: 'top' as const },
    title: 'Configure Your Strategy',
    text: 'Set your autonomy level, max positions, and buying power per trade. Full Manual means you approve every trade. Semi-Auto lets high-confidence trades execute automatically.',
    buttons: [
      {
        text: 'Back',
        classes: BTN_SECONDARY,
        action() {
          (this as unknown as Tour).back();
        },
      },
      {
        text: 'Next',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).next();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
  {
    id: 'btn-run-now',
    attachTo: { element: '[data-tour="btn-run-now"]', on: 'left' as const },
    title: 'Run the Strategist 🎯',
    text: "Hit Run Now to trigger Claude's first market analysis. You'll see trade recommendations appear in the Action Queue tab.",
    buttons: [
      {
        text: 'Finish Tour',
        classes: BTN_PRIMARY,
        action() {
          (this as unknown as Tour).complete();
          onFinish();
        },
      },
    ],
    cancelIcon: { enabled: true },
    scrollTo: true,
  },
];
